import { useState, useEffect, type ReactNode } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import PositionDetails from './pages/PositionDetails';
import CareerSite from './pages/CareerSite';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CandidateLink from './pages/CandidateLink';
import CandidateChecklist from './pages/CandidateChecklist';
import CandidateCertifications from './pages/CandidateCertifications';
import CandidatePrivacyNotice from './pages/CandidatePrivacyNotice';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';
import ErrorPage from './pages/ErrorPage';
import MaintenancePage from './pages/MaintenancePage';
import ShortUrlRedirect from './pages/ShortUrlRedirect';
import ShortUrlInternalRedirect from './pages/ShortUrlInternalRedirect';
import { isCustomDomain, normalizeLegacyQuotedSlugPath } from './utils/routing';
import { apiService } from './services/api';
import RegionGate from './components/RegionGate';
import UtmCapture from './components/UtmCapture';
import CareerGate from './career-site/CareerGate';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolver for any URL segment that contains a UUID.
 * 1. Try fetching as a position → render PositionDetails
 * 2. If 404 → try as a client career site UUID → redirect to career site
 * 3. If both fail → 404
 *
 * For non-UUID segments, falls back to backward-compat redirects.
 */
function UuidResolver({ mode }: { mode: 'path' | 'custom-domain' }) {
  const params = useParams<{ clientCode: string; first: string; second: string }>();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(true);
  const [isPosition, setIsPosition] = useState(false);

  const uuid = params.second;
  const hasUuid = UUID_RE.test(uuid || '');

  useEffect(() => {
    if (!hasUuid) {
      setResolving(false);
      return;
    }

    let cancelled = false;

    (async () => {
      // Try as position first
      const exists = await apiService.checkPositionExists(uuid!);
      if (cancelled) return;

      if (exists) {
        setIsPosition(true);
        setResolving(false);
      } else {
        // Not a position — treat as old client UUID, redirect to career site
        if (mode === 'custom-domain') {
          navigate('/', { replace: true });
        } else {
          navigate(`/${params.clientCode}`, { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [uuid]);

  if (!hasUuid) {
    // Not a UUID — backward-compat redirect
    if (mode === 'custom-domain') {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/${params.clientCode}`} replace />;
  }

  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (isPosition) {
    return <PositionDetails />;
  }

  return null;
}

// Links published with an empty position slug (`/client//uuid`) match no route; collapsed they reach the UUID resolver.
function NotFoundOrCollapseSlashes() {
  const { pathname, search, hash } = useLocation();
  if (/\/{2,}/.test(pathname)) {
    return <Navigate to={pathname.replace(/\/{2,}/g, '/') + search + hash} replace />;
  }
  return <NotFound />;
}

function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  const target = generatePath(to, params as Record<string, string>);
  return <Navigate to={target} replace />;
}

function LegacyQuotedSlugRedirect({ children }: { children: ReactNode }) {
  const { pathname, search, hash } = useLocation();
  const normalizedPath = normalizeLegacyQuotedSlugPath(pathname);

  if (normalizedPath !== pathname) {
    return <Navigate to={normalizedPath + search + hash} replace />;
  }

  return children;
}

function App() {
  // Check if maintenance mode is enabled
  const isMaintenanceMode = import.meta.env.VITE_ORBIO_MAINT_WINDOW_ENABLED === 'true';

  // If maintenance mode is enabled, show maintenance page for all routes
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  const customDomain = isCustomDomain();

  return (
    <Router>
      <LegacyQuotedSlugRedirect>
        <UtmCapture />
        <RegionGate>
        {/* Career sites (`/career/<slug>/…`, the preview, or a custom domain that
            serves one) render from their own lazy chunk; every other URL takes
            today's routes below, untouched. */}
        <CareerGate>
        <Routes>
          {/* Candidate links, opened from a message. Static first segments, so
              React Router ranks them above the dynamic catch-alls below. */}
          <Route path="/candidate/:region/privacy" element={<CandidatePrivacyNotice />} />
          <Route path="/candidate/:region/:code/privacy" element={<CandidatePrivacyNotice />} />
          <Route path="/candidate/:region/:code" element={<CandidateLink />} />
          <Route path="/candidate/:region" element={<CandidateLink />} />
          {/* Old sessions remain usable for their 24–72 hour lifetime. Their
              purpose is intentionally not redirected to the unified endpoint. */}
          <Route path="/checklist/:region/:code" element={<CandidateChecklist />} />
          <Route path="/certifications/:region/:code" element={<CandidateCertifications />} />
        {customDomain ? (
          <>
            {/* Custom domain mode: no client slug in URL */}

            {/* Internal career site routes */}
            <Route path="/internal/:clientUuid" element={<CareerSite isInternal />} />
            <Route path="/internal/:clientUuid/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/internal/:clientUuid/terms-of-service" element={<TermsOfService />} />
            <Route path="/internal/:clientUuid/:slug/:positionUuid" element={<PositionDetails isInternal />} />

            {/* Referral portal routes */}
            <Route path="/referral/:clientUuid" element={<CareerSite isReferral />} />
            <Route path="/referral/:clientUuid/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/referral/:clientUuid/terms-of-service" element={<TermsOfService />} />
            <Route path="/referral/:clientUuid/:slug/:positionUuid" element={<PositionDetails isReferral />} />

            {/* Career site at root */}
            <Route path="/" element={<CareerSite />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Backward compat: old /{clientCode} or /{clientUuid} career site URLs → / */}
            <Route path="/:oldSlug" element={<Navigate to="/" replace />} />
            <Route path="/:oldSlug/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/:oldSlug/terms-of-service" element={<Navigate to="/terms-of-service" replace />} />

            {/* 2-segment: UUID resolver — position first, then client fallback */}
            <Route path="/:first/:second" element={<UuidResolver mode="custom-domain" />} />

            {/* Catch path-mode URLs on custom domain (3 segments) — will redirect to canonical */}
            <Route path="/:companySlug/:slug/:positionUuid" element={<PositionDetails />} />
          </>
        ) : (
          <>
            {/* Path mode: client code is in the URL path */}

            {/* Internal career site routes (for employees) */}
            <Route path="/internal/:clientCode" element={<CareerSite isInternal />} />
            <Route path="/internal/:clientCode/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/internal/:clientCode/terms-of-service" element={<TermsOfService />} />
            <Route path="/internal/:clientCode/:slug/:positionUuid" element={<PositionDetails isInternal />} />

            {/* Referral portal routes */}
            <Route path="/referral/:clientCode" element={<CareerSite isReferral />} />
            <Route path="/referral/:clientCode/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/referral/:clientCode/terms-of-service" element={<TermsOfService />} />
            <Route path="/referral/:clientCode/:slug/:positionUuid" element={<PositionDetails isReferral />} />

            {/* Backward compat: old /{clientCode} internal routes → / */}
            <Route path="/internal/:clientSlug/:clientCode" element={<LegacyRedirect to="/internal/:clientCode" />} />
            <Route
              path="/internal/:clientSlug/:clientCode/privacy-policy"
              element={<LegacyRedirect to="/internal/:clientCode/privacy-policy" />}
            />
            <Route
              path="/internal/:clientSlug/:clientCode/:slug/:positionUuid"
              element={<LegacyRedirect to="/internal/:clientCode/:slug/:positionUuid" />}
            />

            {/* Career site route — single segment: /{clientCode} */}
            <Route path="/:clientCode" element={<CareerSite />} />
            <Route path="/:clientCode/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/:clientCode/terms-of-service" element={<TermsOfService />} />

            {/* Position details: /{clientCode}/{slug}/{positionUuid} */}
            <Route path="/:clientCode/:slug/:positionUuid" element={<PositionDetails />} />

            {/* 2-segment: UUID resolver — position first, then client fallback */}
            <Route path="/:clientCode/:second" element={<UuidResolver mode="path" />} />
          </>
        )}

        {/* AP-code-based career site route (multi-client) — works in both modes */}
        <Route path="/sites/:apCode" element={<CareerSite />} />
        <Route path="/sites/:apCode/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/sites/:apCode/terms-of-service" element={<TermsOfService />} />
        <Route path="/sites/:apCode/:slug/:positionUuid" element={<PositionDetails />} />

        {/* Short URL redirects */}
        <Route path="/p/:positionUuid" element={<ShortUrlRedirect />} />
        <Route path="/p/internal/:positionUuid" element={<ShortUrlInternalRedirect />} />
        <Route path="/p/referral/:positionUuid" element={<ShortUrlInternalRedirect isReferral />} />

        {/* Error routes */}
        <Route path="/404" element={<NotFound />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<NotFoundOrCollapseSlashes />} />
        </Routes>
        </CareerGate>
        </RegionGate>
      </LegacyQuotedSlugRedirect>
    </Router>
  );
}

export default App;
